import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { OnboardingData } from "@/pages/onboarding/Onboarding";
import {
  CircleFadingArrowUp,
  UserX,
  Send,
  Zap,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  TrendingUp,
  Lock,
  type LucideIcon,
} from "lucide-react";
import {
  WORKFLOW_DEFINITIONS,
  TRIAL_TO_PAID,
  ATTENDANCE_CONFIRMATION,
  NO_SHOW_RECOVERY,
  IDLE_LEAD_WINBACK,
} from "@/lib/workflows/definitions";

// Slide 2. Workflow selection.
//
// Shows the seed workflows (the same definitions the dashboard's Workflows
// screen renders) as selectable cards. Each card explains the OUTCOME and the
// LOGIC, with a compact "how it works" chip-flow generated from the same
// definition the engine runs, so the depiction never drifts.
//
// Selections are stored as automation keys in `data.selectedWorkflows`. They are
// recorded as intent here (pre-checkout) and activated after checkout on a paid
// plan by the stripe-webhook. On the Builder plan they stay queued until upgrade
// (the runtime plan gate handles enforcement), so we can record them freely.

// Onboarding-facing metadata per workflow, keyed by the definition's automation
// key. The card icon + outcome line live here; everything else (name, logic,
// steps) comes straight from the workflow definition.
const WORKFLOW_META: Record<string, { icon: LucideIcon; outcome: string }> = {
  [TRIAL_TO_PAID.key]: {
    icon: CircleFadingArrowUp,
    outcome: "More first-time visitors convert into repeat, packaged customers.",
  },
  [ATTENDANCE_CONFIRMATION.key]: {
    icon: Check,
    outcome: "More customers confirm clearly, while uncertain appointments stay in your team's control.",
  },
  [NO_SHOW_RECOVERY.key]: {
    icon: UserX,
    outcome: "Fewer missed bookings slip away — no-shows get nudged to rebook.",
  },
  [IDLE_LEAD_WINBACK.key]: {
    icon: Send,
    outcome: "More new leads turn into booked customers instead of going cold.",
  },
};

// The picks that lead with the strongest, lowest-risk value are pre-selected so
// the common case is one click. Mirrors the dashboard defaults (idle-lead
// win-back is opt-in there too, so it starts unchecked).
export const DEFAULT_SELECTED_WORKFLOWS: string[] = [
  TRIAL_TO_PAID.key,
  ATTENDANCE_CONFIRMATION.key,
  NO_SHOW_RECOVERY.key,
];

interface Props {
  data: OnboardingData;
  updateData: (fields: Partial<OnboardingData>) => void;
  nextStep: () => void;
  prevStep: () => void;
  /** When false, no messaging channel is connected → workflows can't run, so
   *  the cards are disabled and any selection is cleared (Sandbox mode). */
  hasConnectedChannel?: boolean;
}

// One workflow card: header (icon + name + select control), the logic blurb, and
// the outcome line. Disabled (greyed, unclickable) when no channel is connected.
const WorkflowCard = ({
  defKey,
  name,
  description,
  selected,
  onToggle,
  disabled = false,
}: {
  defKey: string;
  name: string;
  description: string;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) => {
  const meta = WORKFLOW_META[defKey];
  const Icon = meta?.icon ?? Zap;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onToggle}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`relative rounded-xl border p-4 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
          : selected
            ? "cursor-pointer border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20"
            : "cursor-pointer border-slate-200 hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">{name}</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
        {/* Select control */}
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${
            selected ? "border-primary bg-primary text-white" : "border-slate-300 bg-white"
          }`}
        >
          {selected && <Check className="h-3.5 w-3.5" />}
        </span>
      </div>

      {/* Outcome */}
      {meta?.outcome && (
        <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-emerald-50/60 px-2.5 py-1.5">
          <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
          <p className="text-[11px] font-medium leading-snug text-emerald-800">{meta.outcome}</p>
        </div>
      )}
    </div>
  );
};

const WorkflowSelectionStep = ({ data, updateData, nextStep, prevStep, hasConnectedChannel = false }: Props) => {
  const selected = data.selectedWorkflows ?? [];

  // No channel → workflows can't run. Clear any pre-selected picks so the
  // business is correctly treated as Sandbox (not "live") downstream.
  useEffect(() => {
    if (!hasConnectedChannel && selected.length > 0) {
      updateData({ selectedWorkflows: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConnectedChannel]);

  const toggle = (key: string) => {
    if (!hasConnectedChannel) return;
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key];
    updateData({ selectedWorkflows: next });
  };

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="space-y-6">
        {hasConnectedChannel ? (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <Sparkles className="h-4 w-4" />
          </span>
          <p className="text-sm leading-relaxed text-emerald-900">
            <span className="font-semibold">Your channel is connected.</span>{" "}
            Pick at least one workflow to go live on your chosen plan — your AI runs
            them automatically. You can turn any of them on or off later.
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <Lock className="h-4 w-4" />
          </span>
          <p className="text-sm leading-relaxed text-amber-900">
            <span className="font-semibold">Connect a messaging channel to turn on workflow automations.</span>{" "}
            Without one, these stay locked and your business runs in{" "}
            <span className="font-semibold">Sandbox mode (S$1/mo)</span>. Go back a step to
            connect WhatsApp, Telegram, or Instagram — or connect later from your dashboard.
          </p>
        </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {WORKFLOW_DEFINITIONS.map((def) => (
            <WorkflowCard
              key={def.key}
              defKey={def.key}
              name={def.name}
              description={def.description}
              selected={selected.includes(def.key)}
              onToggle={() => toggle(def.key)}
              disabled={!hasConnectedChannel}
            />
          ))}
        </div>
      </div>

      <div className="mt-auto flex justify-between">
        <Button type="button" variant="outline" size="sm" onClick={prevStep}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back
        </Button>
        <Button type="button" size="sm" onClick={nextStep}>
          Continue <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default WorkflowSelectionStep;
